import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'

import {
  aanmakenPartij, bijwerkenPartij, fetchVoorraad, verwijderenPartij,
  type VoorraadPartij, type VoorraadPayload,
} from '@/api/voorraad'
import { Tabs } from '@/components/Tabs'
import { KpiCard } from '@/components/KpiCard'
import { useCurrentOrg, useCurrentOrgId } from '@/hooks/useOrganisations'
import { useOrgLijsten } from '@/hooks/useOrgLijsten'
import { datumNL, euro, nlGeheel } from '@/lib/format'

const BUCKETS = ['Verlopen', 'A - Tot 6 maanden', 'B - 6 tot 12 maanden',
                 'C - 12 tot 18 maanden', 'D - Meer dan 18 maanden', 'Geen THT']

function bucketVoor(resterend: number | null | undefined): string {
  if (resterend === null || resterend === undefined) return 'Geen THT'
  if (resterend < 0) return 'Verlopen'
  if (resterend <= 180) return 'A - Tot 6 maanden'
  if (resterend <= 365) return 'B - 6 tot 12 maanden'
  if (resterend <= 548) return 'C - 12 tot 18 maanden'
  return 'D - Meer dan 18 maanden'
}

export function InvoerVoorraadPage() {
  const orgId = useCurrentOrgId()
  const org = useCurrentOrg()
  const bederfelijk = (org?.bederfelijke_waar ?? 'Ja') !== 'Nee'

  if (!orgId) return <Header><Box>Selecteer een klant in de zijbalk.</Box></Header>

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-kh-blue">Invoer voorraad</h1>
        <p className="text-sm text-kh-gray-500 mt-1">{org?.naam}</p>
      </div>

      <Tabs
        tabs={[
          { key: 'overzicht',     label: 'Voorraadoverzicht',       content: <Voorraadoverzicht orgId={orgId} /> },
          { key: 'houdbaar',      label: 'Houdbaarheidsoverzicht',  content: <Houdbaarheid orgId={orgId} />, hidden: !bederfelijk },
          { key: 'locaties',      label: 'Locatieoverzicht',         content: <Locatieoverzicht orgId={orgId} /> },
          { key: 'toevoegen',     label: 'Toevoegen',                content: <Toevoegen orgId={orgId} /> },
          { key: 'bewerken',      label: 'Bewerken/verwijderen',     content: <Bewerken orgId={orgId} /> },
        ]}
      />
    </div>
  )
}


// ── Voorraadoverzicht ───────────────────────────────────────────────────
function Voorraadoverzicht({ orgId }: { orgId: number }) {
  const [zoek, setZoek] = useState('')
  const { data, isLoading } = useQuery({
    queryKey: ['voorraad', orgId, zoek],
    queryFn: () => fetchVoorraad(orgId, { zoek: zoek || undefined }),
  })

  const totaal = useMemo(
    () => (data ?? []).reduce((s, p) => s + (p.totale_waarde ?? 0), 0),
    [data]
  )
  const eenheden = useMemo(
    () => (data ?? []).reduce((s, p) => s + (p.aantal ?? 0), 0),
    [data]
  )

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard label="Partijen" value={nlGeheel(data?.length ?? 0)} accent="blue" />
        <KpiCard label="Totale waarde" value={euro(totaal)} />
        <KpiCard label="Eenheden" value={nlGeheel(eenheden)} accent="blue" />
      </div>

      <input className="input-field" placeholder="🔍 Zoek (partijnr, omschrijving, crediteur, factuurnr…)"
             value={zoek} onChange={(e) => setZoek(e.target.value)} />

      {isLoading ? <Box>Laden…</Box>
       : !data || data.length === 0 ? <Box>Geen partijen.</Box>
       : <VoorraadTabel rijen={data} />}
    </div>
  )
}

function VoorraadTabel({ rijen }: { rijen: VoorraadPartij[] }) {
  return (
    <div className="card overflow-x-auto">
      <table className="w-full text-sm">
        <Thead cols={['Partij', 'Productomschrijving', 'Aantal', 'Eenheid', 'Waarde/eenheid',
                       'Totale waarde', 'THT', 'Locatie', 'Voorraadstatus', 'Crediteur', 'Factuurnr.']} />
        <tbody className="divide-y divide-kh-gray-100">
          {rijen.map((p) => (
            <tr key={p.id}>
              <td className="px-4 py-2 font-mono text-xs">{p.partijnummer}</td>
              <td className="px-4 py-2">{p.productomschrijving ?? ''}</td>
              <td className="px-4 py-2 text-right">{nlGeheel(p.aantal)}</td>
              <td className="px-4 py-2">{p.eenheid ?? ''}</td>
              <td className="px-4 py-2 text-right">{euro(p.waarderingsprijs_per_eenheid)}</td>
              <td className="px-4 py-2 text-right font-medium">{euro(p.totale_waarde)}</td>
              <td className="px-4 py-2 text-xs">
                {datumNL(p.tht_datum)}
                {p.resterende_dagen !== null && p.resterende_dagen !== undefined && (
                  <span className={`block text-[10px] ${p.resterende_dagen < 0 ? 'text-red-600' :
                                    p.resterende_dagen < 180 ? 'text-orange-600' : 'text-kh-gray-400'}`}>
                    {p.resterende_dagen < 0 ? 'verlopen' : `${p.resterende_dagen}d`}
                  </span>
                )}
              </td>
              <td className="px-4 py-2 text-xs">{p.opslaglocatie ?? ''}</td>
              <td className="px-4 py-2 text-xs">{p.voorraadstatus ?? ''}</td>
              <td className="px-4 py-2 text-xs">{p.crediteur ?? ''}</td>
              <td className="px-4 py-2 text-xs font-mono">{p.inkoopfactuurnr ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Houdbaarheid ────────────────────────────────────────────────────────
function Houdbaarheid({ orgId }: { orgId: number }) {
  const [zoek, setZoek] = useState('')
  const { data } = useQuery({
    queryKey: ['voorraad-houdbaar', orgId, zoek],
    queryFn: () => fetchVoorraad(orgId, { zoek: zoek || undefined }),
  })
  const groep = useMemo(() => {
    const m: Record<string, { partijen: number; waarde: number }> = {}
    for (const b of BUCKETS) m[b] = { partijen: 0, waarde: 0 }
    let totaal = 0
    for (const p of data ?? []) {
      const b = bucketVoor(p.resterende_dagen)
      m[b].partijen += 1
      m[b].waarde += p.totale_waarde
      totaal += p.totale_waarde
    }
    return { buckets: m, totaal }
  }, [data])

  return (
    <div className="space-y-4">
      <input className="input-field" placeholder="🔍 Zoek partij (partijnr of omschrijving)"
             value={zoek} onChange={(e) => setZoek(e.target.value)} />

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <Thead cols={['Klasse', 'Partijen', 'Waarde', '% van totaal']} />
          <tbody className="divide-y divide-kh-gray-100">
            {BUCKETS.filter((b) => groep.buckets[b].partijen > 0).map((b) => (
              <tr key={b}>
                <td className="px-4 py-2.5">{b}</td>
                <td className="px-4 py-2.5 text-right">{nlGeheel(groep.buckets[b].partijen)}</td>
                <td className="px-4 py-2.5 text-right">{euro(groep.buckets[b].waarde)}</td>
                <td className="px-4 py-2.5 text-right">
                  {groep.totaal > 0 ? ((groep.buckets[b].waarde / groep.totaal) * 100).toFixed(1) : '0.0'}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Locatieoverzicht ────────────────────────────────────────────────────
function Locatieoverzicht({ orgId }: { orgId: number }) {
  const [zoek, setZoek] = useState('')
  const { data } = useQuery({
    queryKey: ['voorraad-locatie', orgId, zoek],
    queryFn: () => fetchVoorraad(orgId, { zoek: zoek || undefined }),
  })

  const liggend = (data ?? []).filter((p) => p.voorraadstatus === 'Liggend')
  const zeilend = (data ?? []).filter((p) => p.voorraadstatus === 'Zeilend')
  const totLig = liggend.reduce((s, p) => s + p.totale_waarde, 0)
  const totZei = zeilend.reduce((s, p) => s + p.totale_waarde, 0)
  const tot = totLig + totZei

  return (
    <div className="space-y-4">
      <input className="input-field" placeholder="🔍 Zoek partij/locatie/crediteur"
             value={zoek} onChange={(e) => setZoek(e.target.value)} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard label="Op locatie (liggend)" value={euro(totLig)} />
        <KpiCard label="Onderweg (zeilend)" value={euro(totZei)} />
        <KpiCard label="% onderweg" value={tot > 0 ? `${((totZei / tot) * 100).toFixed(1)}%` : '0%'} accent="gray" />
      </div>

      {liggend.length > 0 && (
        <div>
          <h3 className="font-semibold text-kh-blue mb-2">Liggend ({liggend.length})</h3>
          <VoorraadTabel rijen={liggend} />
        </div>
      )}
      {zeilend.length > 0 && (
        <div>
          <h3 className="font-semibold text-kh-blue mb-2">Zeilend ({zeilend.length})</h3>
          <VoorraadTabel rijen={zeilend} />
        </div>
      )}
      {(data ?? []).length === 0 && <Box>Geen partijen gevonden.</Box>}
    </div>
  )
}

// ── Toevoegen ───────────────────────────────────────────────────────────
function leegPayload(): VoorraadPayload {
  return {
    partijnummer: '', productomschrijving: '',
    aantal: 0, eenheid: '', waarderingsprijs_per_eenheid: 0, totale_waarde: null,
    inslagdatum: new Date().toISOString().slice(0, 10),
    tht_datum: null,
    opslaglocatie: '', voorraadstatus: 'Liggend', orderstatus: 'Onverkocht', ordernummer: '',
    crediteur: '', crediteurnummer: '', inkoopfactuurnr: '', opmerkingen: '',
  }
}

function PartijForm({
  initial, onSubmit, submitLabel, orgId,
}: {
  initial: VoorraadPayload
  onSubmit: (data: VoorraadPayload) => void
  submitLabel: string
  orgId: number
}) {
  const [form, setForm] = useState<VoorraadPayload>(initial)
  const [fout, setFout] = useState<string | null>(null)
  const { data: lijsten } = useOrgLijsten(orgId)

  const submit = () => {
    setFout(null)
    if (!form.partijnummer.trim()) return setFout('Partijnummer is verplicht')
    if (!form.inkoopfactuurnr.trim()) return setFout('Inkoopfactuurnr is verplicht')
    if (!form.aantal || form.aantal <= 0) return setFout('Aantal moet > 0')
    if (!form.waarderingsprijs_per_eenheid || form.waarderingsprijs_per_eenheid <= 0)
      return setFout('Waarderingsprijs moet > 0')
    onSubmit(form)
  }

  const opt = (cat: string) => lijsten?.[cat] ?? []

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Partijnummer *">
          <input className="input-field" value={form.partijnummer}
                 onChange={(e) => setForm({ ...form, partijnummer: e.target.value })} />
        </Field>
        <Field label="Productomschrijving *">
          <input className="input-field" value={form.productomschrijving ?? ''}
                 onChange={(e) => setForm({ ...form, productomschrijving: e.target.value })} />
        </Field>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Aantal *">
          <input className="input-field" type="number" step="0.01" value={form.aantal ?? 0}
                 onChange={(e) => setForm({ ...form, aantal: Number(e.target.value) })} />
        </Field>
        <Field label="Eenheid">
          <select className="input-field" value={form.eenheid ?? ''}
                  onChange={(e) => setForm({ ...form, eenheid: e.target.value })}>
            <option value="">— selecteer —</option>
            {opt('Eenheid').map((o) => <option key={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Waarderingsprijs per eenheid (€) *">
          <input className="input-field" type="number" step="0.01" value={form.waarderingsprijs_per_eenheid ?? 0}
                 onChange={(e) => setForm({ ...form, waarderingsprijs_per_eenheid: Number(e.target.value) })} />
        </Field>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Inslagdatum">
          <input className="input-field" type="date" value={form.inslagdatum ?? ''}
                 onChange={(e) => setForm({ ...form, inslagdatum: e.target.value || null })} />
        </Field>
        <Field label="Houdbaarheidsdatum">
          <input className="input-field" type="date" value={form.tht_datum ?? ''}
                 onChange={(e) => setForm({ ...form, tht_datum: e.target.value || null })} />
        </Field>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Opslaglocatie">
          <select className="input-field" value={form.opslaglocatie ?? ''}
                  onChange={(e) => setForm({ ...form, opslaglocatie: e.target.value })}>
            <option value="">— selecteer —</option>
            {opt('Opslaglocatie').map((o) => <option key={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Voorraadstatus">
          <select className="input-field" value={form.voorraadstatus ?? 'Liggend'}
                  onChange={(e) => setForm({ ...form, voorraadstatus: e.target.value })}>
            {(opt('Voorraadstatus').length ? opt('Voorraadstatus') : ['Liggend', 'Zeilend']).map((o) => <option key={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Orderstatus">
          <select className="input-field" value={form.orderstatus ?? 'Onverkocht'}
                  onChange={(e) => setForm({ ...form, orderstatus: e.target.value })}>
            {(opt('Orderstatus').length ? opt('Orderstatus') : ['Onverkocht', 'Voorverkocht']).map((o) => <option key={o}>{o}</option>)}
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Crediteur">
          <input className="input-field" list="crediteur-opties" value={form.crediteur ?? ''}
                 onChange={(e) => setForm({ ...form, crediteur: e.target.value })} />
          <datalist id="crediteur-opties">
            {opt('Crediteur').map((o) => <option key={o} value={o} />)}
          </datalist>
        </Field>
        <Field label="Crediteurnummer">
          <input className="input-field" value={form.crediteurnummer ?? ''}
                 onChange={(e) => setForm({ ...form, crediteurnummer: e.target.value })} />
        </Field>
        <Field label="Inkoopfactuurnr. *">
          <input className="input-field" value={form.inkoopfactuurnr ?? ''}
                 onChange={(e) => setForm({ ...form, inkoopfactuurnr: e.target.value })} />
        </Field>
      </div>
      <Field label="Ordernummer">
        <input className="input-field" value={form.ordernummer ?? ''}
               onChange={(e) => setForm({ ...form, ordernummer: e.target.value })} />
      </Field>
      <Field label="Opmerkingen">
        <textarea className="input-field" rows={2} value={form.opmerkingen ?? ''}
                  onChange={(e) => setForm({ ...form, opmerkingen: e.target.value })} />
      </Field>
      {fout && <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">{fout}</div>}
      <div>
        <button className="btn-primary" onClick={submit}>{submitLabel}</button>
      </div>
    </div>
  )
}

function Toevoegen({ orgId }: { orgId: number }) {
  const qc = useQueryClient()
  const [success, setSuccess] = useState<string | null>(null)
  const mut = useMutation({
    mutationFn: (data: VoorraadPayload) => aanmakenPartij(orgId, data),
    onSuccess: (p) => { qc.invalidateQueries({ queryKey: ['voorraad'] }); setSuccess(`✓ Partij ${p.partijnummer} toegevoegd`) },
    onError: (e: any) => alert('Fout: ' + (e?.response?.data?.detail ?? e.message)),
  })
  return (
    <div className="card p-5 space-y-4">
      {success && <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-700">{success}</div>}
      <PartijForm initial={leegPayload()} orgId={orgId}
                  onSubmit={(d) => { setSuccess(null); mut.mutate(d) }}
                  submitLabel="Partij toevoegen" />
    </div>
  )
}

// ── Bewerken ────────────────────────────────────────────────────────────
function Bewerken({ orgId }: { orgId: number }) {
  const qc = useQueryClient()
  const [zoek, setZoek] = useState('')
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const { data } = useQuery({
    queryKey: ['voorraad-bew', orgId, zoek],
    queryFn: () => fetchVoorraad(orgId, { zoek: zoek || undefined }),
  })
  const selected = (data ?? []).find((p) => p.id === selectedId)

  const bewerken = useMutation({
    mutationFn: (d: VoorraadPayload) => bijwerkenPartij(orgId, selectedId!, d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['voorraad'] }); alert('✓ Opgeslagen') },
  })
  const verwijderen = useMutation({
    mutationFn: () => verwijderenPartij(orgId, selectedId!),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['voorraad'] }); setSelectedId(null) },
  })

  return (
    <div className="space-y-4">
      <input className="input-field" placeholder="🔍 Zoek partij om te bewerken"
             value={zoek} onChange={(e) => setZoek(e.target.value)} />

      {!data || data.length === 0 ? <Box>Geen partijen.</Box> : (
        <Field label={`Partijnummer (${data.length} resultaten)`}>
          <select className="input-field max-w-2xl" value={selectedId ?? ''}
                  onChange={(e) => setSelectedId(e.target.value ? Number(e.target.value) : null)}>
            <option value="">— selecteer —</option>
            {data.map((p) => (
              <option key={p.id} value={p.id}>{p.partijnummer} — {p.productomschrijving ?? ''}</option>
            ))}
          </select>
        </Field>
      )}

      {selected && (
        <div className="card p-5 space-y-4">
          <PartijForm
            initial={{
              partijnummer: selected.partijnummer, productomschrijving: selected.productomschrijving,
              aantal: selected.aantal, eenheid: selected.eenheid,
              waarderingsprijs_per_eenheid: selected.waarderingsprijs_per_eenheid,
              totale_waarde: null,
              inslagdatum: selected.inslagdatum, tht_datum: selected.tht_datum,
              opslaglocatie: selected.opslaglocatie, voorraadstatus: selected.voorraadstatus,
              orderstatus: selected.orderstatus, ordernummer: selected.ordernummer,
              crediteur: selected.crediteur, crediteurnummer: selected.crediteurnummer,
              inkoopfactuurnr: selected.inkoopfactuurnr ?? '',
              opmerkingen: selected.opmerkingen,
            }}
            orgId={orgId}
            onSubmit={(d) => bewerken.mutate(d)}
            submitLabel="Wijzigingen opslaan"
          />
          <div className="border-t border-kh-gray-200 pt-3">
            <button className="text-red-600 hover:text-red-800 text-sm inline-flex items-center gap-1"
                    onClick={() => confirm(`Partij '${selected.partijnummer}' verwijderen?`) && verwijderen.mutate()}>
              <Trash2 size={14} /> Verwijderen
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Helpers ─────────────────────────────────────────────────────────────
function Header({ children }: { children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-kh-blue">Invoer voorraad</h1>
      </div>
      {children}
    </div>
  )
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="block font-medium text-kh-gray-700 mb-1">{label}</span>
      {children}
    </label>
  )
}
function Thead({ cols }: { cols: string[] }) {
  return (
    <thead className="bg-kh-gray-50 text-kh-gray-600 uppercase text-xs tracking-wider">
      <tr>{cols.map((c, i) => <th key={i} className="text-left px-4 py-3 whitespace-nowrap">{c}</th>)}</tr>
    </thead>
  )
}
function Box({ children }: { children: React.ReactNode }) {
  return <div className="card p-6 text-center text-kh-gray-500">{children}</div>
}
